import React from 'react';
import { Link } from 'react-router-dom';
import ArticleLayout from '../components/ArticleLayout';
import { PAGES } from '../lib/site';
import { getAlzheimerDiagnosticEvidenceById } from '../lib/alzheimerDiagnosticEvidence';

const page = PAGES.find((p) => p.path === '/diagnostica-alzheimer');
const criteria2024 = getAlzheimerDiagnosticEvidenceById('alz-association-2024-revised-criteria');
const detect = getAlzheimerDiagnosticEvidenceById('detect-adrd-clinical-evaluation');
const nice = getAlzheimerDiagnosticEvidenceById('nice-ng97-dementia-assessment');

const ANSWER =
  'Não. O Mini-Mental não diagnostica doença de Alzheimer e também não a exclui quando o escore está preservado. Ele mede desempenho cognitivo naquele contexto de aplicação. Quando existe suspeita clínica, o resultado é integrado à história, funcionalidade, exame e investigação etiológica. Atualmente, Alzheimer pode ser definido biologicamente por biomarcadores apropriados; o MEEM não é um desses biomarcadores.';

const REFERENCES = [criteria2024.citation, detect.citation, nice.citation];

const RELATED = [
  { to: '/interpretacao-dos-resultados', label: 'Como interpretar o resultado do Mini Mental', note: 'organize referência, condições da aplicação, funcionalidade e evolução antes de inferir doença.' },
  { to: '/pontos-de-corte', label: 'Pontos de corte e referências', note: 'um limiar de rastreio não estabelece etiologia.' },
  { to: '/acompanhamento-e-queda-de-pontos', label: 'Queda de pontos no acompanhamento', note: 'mudança longitudinal precisa ser interpretada com variabilidade e contexto.' },
  { to: '/mini-mental-ou-moca', label: 'Mini Mental ou MoCA', note: 'quando um instrumento breve preservado não encerra a investigação.' },
  { to: '/adaptacoes-sensoriais', label: 'Adaptações sensoriais e motoras', note: 'barreiras de acesso ao teste podem alterar o desempenho sem representar neurodegeneração.' }
];

const CONTRIBUTORS = [
  'delirium e outros quadros agudos',
  'depressão e outros transtornos que afetam desempenho',
  'efeitos de medicamentos e carga anticolinérgica',
  'déficits visuais ou auditivos',
  'condições metabólicas, sistêmicas ou neurológicas concorrentes',
  'escolaridade e outras características que modificam a interpretação do teste'
];

export default function DiagnosticaAlzheimer() {
  return (
    <ArticleLayout page={page} answer={ANSWER} related={RELATED} references={REFERENCES}>
      <section aria-labelledby="baixo-nao-causa">
        <h2 id="baixo-nao-causa" className="text-2xl mb-5">Um escore baixo não informa a causa</h2>
        <p className="text-[var(--text-muted)]">
          O MEEM descreve desempenho cognitivo em uma aplicação. Ele não distingue sozinho doença de Alzheimer,
          outras doenças neurodegenerativas, delirium, depressão, efeitos de medicamentos, barreiras sensoriais
          ou outras condições capazes de reduzir a pontuação.
        </p>
      </section>

      <section aria-labelledby="normal-nao-exclui">
        <h2 id="normal-nao-exclui" className="text-2xl mb-5">Um escore preservado também não exclui doença</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Um instrumento cognitivo breve pode permanecer dentro da faixa esperada apesar de uma queixa relevante
          ou de alterações iniciais. A orientação do NICE é não excluir demência apenas porque um teste cognitivo
          estruturado teve resultado normal.
        </p>
        <p className="text-[var(--text-muted)]">
          Quando a história ou a funcionalidade mantêm a suspeita, o próximo passo é aprofundar a avaliação, e não
          transformar o escore em uma regra de exclusão. Veja também{' '}
          <Link to="/interpretacao-dos-resultados" className=" text-[var(--primary)] hover:underline">
            como interpretar o resultado
          </Link>.
        </p>
      </section>

      <section aria-labelledby="sindrome-primeiro">
        <h2 id="sindrome-primeiro" className="text-2xl mb-5">Primeiro: existe uma síndrome cognitiva?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          O DETeCD-ADRD organiza a avaliação de pessoas sintomáticas em camadas: história da mudança, relato de um
          informante quando disponível, desempenho cognitivo, impacto funcional, exame e caracterização da síndrome
          cognitivo-comportamental.
        </p>
        <p className="text-[var(--text-muted)]">
          Essa etapa responde se há um problema cognitivo clinicamente relevante e qual é seu impacto. O MEEM pode
          contribuir com essa caracterização, mas não substitui o restante da avaliação.
        </p>
      </section>

      <section aria-labelledby="etiologia-depois">
        <h2 id="etiologia-depois" className="text-2xl mb-5">Depois: qual é a provável causa?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Definir uma síndrome não é o mesmo que definir sua etiologia. Antes de atribuir o quadro a Alzheimer, é
          necessário considerar condições que podem causar, agravar ou confundir o comprometimento cognitivo.
        </p>
        <ul className="space-y-3 text-[var(--text-muted)] list-disc pl-5">
          {CONTRIBUTORS.map((item) => <li key={item}>{item}</li>)}
        </ul>
      </section>

      <section aria-labelledby="exames-complementares">
        <h2 id="exames-complementares" className="text-2xl mb-5">Onde entram exames, imagem e neuropsicologia?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Eles entram conforme a pergunta clínica. Exames laboratoriais podem investigar causas contribuintes;
          neuroimagem estrutural pode ajudar a excluir outras causas e a caracterizar o quadro; avaliação
          neuropsicológica pode ser útil quando não está claro se existe comprometimento, qual seu perfil ou qual
          subtipo melhor explica a síndrome.
        </p>
        <p className="text-[var(--text-muted)]">
          Nenhum desses passos deve ser disparado automaticamente por um número isolado do MEEM.
        </p>
      </section>

      <section aria-labelledby="biomarcadores">
        <h2 id="biomarcadores" className="text-2xl mb-5">E os biomarcadores de Alzheimer?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Os critérios revisados de 2024 da Alzheimer&apos;s Association definem a doença de Alzheimer como um processo
          biológico. Nessa estrutura, determinados biomarcadores Core 1 anormais podem estabelecer a presença
          biológica da doença quando usados com desempenho diagnóstico adequado.
        </p>
        <p className="text-[var(--text-muted)] mb-4">
          Isso não transforma o MEEM em teste etiológico: ele não mede amiloide nem tau e não é um biomarcador de
          Alzheimer. Também não significa que a presença biológica determine sozinha a gravidade clínica, porque
          estágio biológico e manifestação clínica não são equivalentes.
        </p>
        <p className="text-[var(--text-muted)]">
          Os próprios critérios não propõem avaliação biomarcadora assistencial de rotina em pessoas assintomáticas.
          A indicação depende do contexto clínico e da utilidade da informação para a decisão.
        </p>
      </section>

      <section aria-labelledby="demencia-alzheimer">
        <h2 id="demencia-alzheimer" className="text-2xl mb-5">Demência e Alzheimer não são sinônimos</h2>
        <p className="text-[var(--text-muted)]">
          Demência descreve uma síndrome clínica em que alterações cognitivas interferem de forma relevante na
          independência funcional. Alzheimer é uma etiologia específica. Por isso, um escore baixo não equivale nem
          a demência nem, menos ainda, a doença de Alzheimer.
        </p>
      </section>

      <section aria-labelledby="quando-aprofundar">
        <h2 id="quando-aprofundar" className="text-2xl mb-5">Quando aprofundar a investigação</h2>
        <ul className="space-y-3 text-[var(--text-muted)] list-disc pl-5">
          <li>queixa cognitiva consistente do paciente, familiar ou outro informante;</li>
          <li>perda funcional ou mudança de autonomia em relação ao desempenho prévio;</li>
          <li>declínio longitudinal ou discrepância entre escore e contexto clínico;</li>
          <li>suspeita persistente apesar de um instrumento breve preservado;</li>
          <li>alteração aguda ou flutuante do estado mental, que exige avaliação da causa aguda em vez de apenas repetir o rastreio.</li>
        </ul>
        <p className="text-[var(--text-muted)] mt-4">
          Para mudanças seriadas, veja{' '}
          <Link to="/acompanhamento-e-queda-de-pontos" className=" text-[var(--primary)] hover:underline">
            queda de pontos no acompanhamento
          </Link>. Para diferenças entre instrumentos, veja{' '}
          <Link to="/mini-mental-ou-moca" className=" text-[var(--primary)] hover:underline">Mini Mental ou MoCA</Link>.
        </p>
      </section>

      <section aria-labelledby="fluxo-final">
        <h2 id="fluxo-final" className="text-2xl mb-5">Fluxo prático</h2>
        <p className="text-[var(--text-muted)] font-medium mb-4">
          queixa ou mudança → caracterizar cognição + função → investigar condições contribuintes → definir síndrome → investigar etiologia → biomarcadores quando indicados → integrar o diagnóstico
        </p>
        <p className="text-[var(--text-muted)]">
          O ponto de corte usado no rastreio continua sendo uma questão separada; veja{' '}
          <Link to="/pontos-de-corte" className=" text-[var(--primary)] hover:underline">pontos de corte</Link>. Se houver
          limitação visual, auditiva ou motora, veja{' '}
          <Link to="/adaptacoes-sensoriais" className=" text-[var(--primary)] hover:underline">adaptações sensoriais</Link>.
        </p>
      </section>
    </ArticleLayout>
  );
}
